
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { ShoppingCart, Check, X, Trash2, Receipt, Eye } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { PurchaseReceiptDialog } from "@/components/purchases/PurchaseReceiptDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const PurchaseRequestsSection = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string>("");

  const { data: purchaseRequests = [], isLoading } = useQuery({
    queryKey: ["mobile-purchase-requests"],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from("product_purchase_requests")
        .select(`
          *,
          product:professional_products!fk_product(*),
          buyer:profiles!product_purchase_requests_buyer_id_fkey(*)
        `)
        .eq("professional_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user
  });

  const updateRequestStatus = useMutation({
    mutationFn: async ({ requestId, status }: { requestId: string; status: string }) => {
      const { data, error } = await supabase
        .from("product_purchase_requests")
        .update({ status })
        .eq("id", requestId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["mobile-purchase-requests"] });
      const statusText = variables.status === 'approved' ? 'approuvée' : 'refusée';
      toast.success(`Demande ${statusText} avec succès`);
    },
    onError: (error) => {
      console.error('Error updating request:', error);
      toast.error("Impossible de mettre à jour le statut de la demande");
    },
  });

  const deleteRequest = useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabase
        .from("product_purchase_requests")
        .delete()
        .eq("id", requestId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mobile-purchase-requests"] });
      toast.success("Demande supprimée avec succès");
    },
    onError: (error) => {
      console.error('Error deleting request:', error);
      toast.error("Impossible de supprimer la demande");
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Approuvée';
      case 'rejected':
        return 'Refusée';
      default:
        return 'En attente';
    }
  };

  const handleViewReceipt = (requestId: string) => {
    setSelectedRequestId(requestId);
    setShowReceiptDialog(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Demandes d'achat</h2>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
          <ShoppingCart className="h-4 w-4 mr-2" />
          Gérer les demandes
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {[
          {
            label: "Total",
            value: purchaseRequests?.length || 0,
            color: "blue"
          },
          {
            label: "Approuvées",
            value: purchaseRequests?.filter(r => r.status === 'approved').length || 0,
            color: "green"
          },
          {
            label: "En Attente",
            value: purchaseRequests?.filter(r => r.status === 'pending').length || 0,
            color: "yellow"
          },
          {
            label: "Refusées",
            value: purchaseRequests?.filter(r => r.status === 'rejected').length || 0,
            color: "red"
          }
        ].map(({ label, value, color }) => (
          <Card key={label}>
            <CardContent className="p-3 text-center">
              <div className={`text-lg font-bold text-${color}-600`}>{value}</div>
              <div className="text-xs text-gray-500">{label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-4">
          <h3 className="text-base font-semibold mb-3">Demandes Récentes</h3>
          <div className="space-y-3">
            {purchaseRequests?.slice(0, 5).map((request, idx) => (
              <div key={idx} className="flex flex-col gap-3 p-3 border rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">{request.product?.title || 'Produit supprimé'}</div>
                    <div className="text-sm text-gray-500">
                      Par {request.buyer?.full_name || 'Utilisateur inconnu'}
                    </div>
                    <div className="text-xs text-gray-400">
                      {format(new Date(request.created_at), "PPPp", { locale: fr })}
                    </div>
                  </div>
                  <Badge className={getStatusColor(request.status)}>
                    {getStatusText(request.status)}
                  </Badge>
                </div>

                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Quantité:</span>
                    <span>{request.quantity}</span>
                  </div>
                  {request.product?.price && (
                    <div className="flex justify-between font-medium">
                      <span>Total:</span>
                      <span>{(request.product.price * request.quantity).toFixed(2)}€</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 flex-wrap">
                  {request.status === 'pending' && (
                    <>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => updateRequestStatus.mutate({
                          requestId: request.id,
                          status: 'approved'
                        })}
                        disabled={updateRequestStatus.isPending}
                        className="flex items-center gap-1 flex-1"
                      >
                        <Check className="h-4 w-4" />
                        Approuver
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateRequestStatus.mutate({
                          requestId: request.id,
                          status: 'rejected'
                        })}
                        disabled={updateRequestStatus.isPending}
                        className="flex items-center gap-1 flex-1"
                      >
                        <X className="h-4 w-4" />
                        Refuser
                      </Button>
                    </>
                  )}

                  {request.status === 'approved' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewReceipt(request.id)}
                      className="flex items-center gap-1"
                    >
                      <Receipt className="h-4 w-4" />
                      Voir le reçu
                    </Button>
                  )}

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={deleteRequest.isPending}
                        className="flex items-center gap-1"
                      >
                        <Trash2 className="h-4 w-4" />
                        Supprimer
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer la demande</AlertDialogTitle>
                        <AlertDialogDescription>
                          Êtes-vous sûr de vouloir supprimer cette demande d'achat ? Cette action est irréversible.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => deleteRequest.mutate(request.id)}
                          disabled={deleteRequest.isPending}
                        >
                          {deleteRequest.isPending ? "Suppression..." : "Supprimer"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            )) || <div className="text-center py-4 text-gray-500">Aucune demande d'achat</div>}
          </div>
        </CardContent>
      </Card>

      <PurchaseReceiptDialog
        open={showReceiptDialog}
        onOpenChange={setShowReceiptDialog}
        purchaseRequestId={selectedRequestId}
      />
    </div>
  );
};
